import { useEffect, useRef } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { isUuid } from '@leanin/shared';
import { asUserId } from '@leanin/shared/ids';
import { trackProfileViewed } from '@/lib/analytics/events';
import { useMember } from '@/lib/use-session';
import {
  mapProfileViewToProfile,
  memberSinceFromCreatedAt,
} from '@/components/profile/mapper';
import {
  ProfileChrome,
  type ProfileHeaderData,
} from '@/components/profile/ProfileChrome';
import { ProfileChromeSkeleton } from '@/components/profile/ProfileChromeSkeleton';
import { ProfileContentView } from '@/components/profile/ProfileContentView';
import { ErrorState, useDelayedLoading } from '@leanin/ui';
import { RetryButton } from '@/components/states/index';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { isNotFoundApiError } from '@/lib/api-error';
import { identityKeys } from '@/lib/queries/identity';
import { groupKeys } from '@/lib/queries/groups';
import { useInfiniteAuthorPosts } from '@/lib/queries/feed';
import { authoredPostToFeedPost } from '@/components/feed/feed-item-to-post';
import { useFormatDate } from '@/lib/format-date.client';
import type { Profile } from '@/components/profile/types';

export const Route = createFileRoute('/_authed/members/$id')({
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: MemberProfilePage,
});

function MemberProfilePage() {
  const t = useTranslations('Profile');
  const fmt = useFormatDate();
  const { id } = Route.useParams();
  const userId = asUserId(id);
  const viewer = useMember();

  const memberQuery = useQuery(identityKeys.member(userId));
  const followingQuery = useQuery(identityKeys.follow(userId));
  const mutualsQuery = useQuery(identityKeys.memberMutuals(userId));
  // Member-scoped listings work for self and others alike: the server
  // authorizes a self listing by membership and scopes anyone else's to
  // what this viewer may discover (plus the member's "Show my Circles on
  // my profile" toggle). pageSize 100 = the complete-list window used by
  // the self profile aside (see /profile — the aside is a complete list,
  // not a teaser).
  const circlesQuery = useQuery(
    groupKeys.circle.forMember(userId, { pageSize: 100 }),
  );
  const networksQuery = useQuery(groupKeys.network.forMember(userId));
  const postsQuery = useInfiniteAuthorPosts(userId, { pageSize: 5 });

  const viewedProfileIdRef = useRef<string | null>(null);
  const profileId = memberQuery.data?.id;
  useEffect(() => {
    if (profileId === undefined || viewer === null) return;
    if (viewedProfileIdRef.current === profileId) return;
    viewedProfileIdRef.current = profileId;
    trackProfileViewed({ is_own_profile: viewer.id === profileId });
  }, [profileId, viewer]);

  const showSkeleton = useDelayedLoading(memberQuery.isLoading || viewer === null);
  if (showSkeleton) return <ProfileChromeSkeleton />;
  if (memberQuery.isLoading || viewer === null) return null;
  // 404 → branded NotFoundPage (member doesn't exist or is deactivated).
  // Other failures keep the retry surface. We render the page directly
  // (rather than `throw notFound()` from a render-context useQuery, which
  // TanStack Router routes to its catch-boundary, not its 404 surface).
  if (isNotFoundApiError(memberQuery.error)) return <NotFoundPage />;
  if (memberQuery.isError || !memberQuery.data) {
    return (
      <ErrorState
        title={t('errors.loadTitle')}
        body={t('errors.loadBody')}
        primary={<RetryButton />}
      />
    );
  }

  const memberData = memberQuery.data;
  const isFollowing = followingQuery.data ?? false;
  const isViewer = viewer.id === memberData.id;

  const headlineRole = memberData.jobTitle ?? '';
  const headlineCompany = memberData.company ?? '';
  const headline =
    headlineRole && headlineCompany
      ? t('hero.headline', { role: headlineRole, company: headlineCompany })
      : headlineRole || headlineCompany || memberData.bio?.slice(0, 80) || '';

  // Built from the resolved member identity immediately — never gated on the
  // collateral queries. Each secondary region (posts, mutuals / circles /
  // networks) fills in independently and paints its own skeleton while in
  // flight, so a slow or failing collateral query can't freeze the profile.
  const mutuals = mutualsQuery.data ?? null;
  const posts = postsQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const circles = circlesQuery.data?.items ?? [];
  const networks = networksQuery.data?.items ?? [];

  const header: ProfileHeaderData = {
    id: memberData.id,
    name:
      [memberData.firstName, memberData.lastName]
        .filter(Boolean)
        .join(' ')
        .trim() || t('memberFallback'),
    headline,
    location: memberData.location ?? '',
    memberSince: memberSinceFromCreatedAt(memberData.createdAt, fmt.monthYear),
    photo: memberData.photoUrl ?? undefined,
    coverPhotoUrl: memberData.coverPhotoUrl ?? undefined,
    isViewer,
    isFollowing,
    isNetworkLeader: networks.some((n) => n.role === 'leader'),
  };

  const profile: Profile = mapProfileViewToProfile(memberData, isFollowing, {
    circles: circles.map((c) => ({
      id: c.id,
      name: c.name,
      role: c.role === 'leader' ? 'Leader' : 'Member',
      meta: t('circle.memberCount', { count: c.memberCount }),
      imageUrl: c.imageUrl,
    })),
    networks: networks.map((n) => ({
      id: n.id,
      name: n.name,
      role: n.role === 'leader' ? ('Leader' as const) : ('Member' as const),
      memberCount: n.memberCount,
      imageUrl: n.imageUrl,
    })),
    posts: posts.map((p) => authoredPostToFeedPost(p, fmt.relativeShort)),
    mutuals: (mutuals?.mutualMembers ?? []).slice(0, 6).map((m) => ({
      id: m.id,
      name: `${m.firstName} ${m.lastName}`.trim(),
      photo: m.photoUrl ?? '',
    })),
  }, t('memberFallback'), fmt.monthYear);

  // The aside shows circles + networks for every profile now; mutuals only
  // appear on someone else's.
  const asideLoading =
    circlesQuery.isLoading ||
    networksQuery.isLoading ||
    (!isViewer && mutualsQuery.isLoading);

  return (
    <ProfileChrome header={header}>
      <ProfileContentView
        profile={profile}
        postsLoading={postsQuery.isLoading}
        asideLoading={asideLoading}
        hasMorePosts={postsQuery.hasNextPage}
        loadingMorePosts={postsQuery.isFetchingNextPage}
        onLoadMorePosts={() => void postsQuery.fetchNextPage()}
      />
    </ProfileChrome>
  );
}
